import { SortFn } from '../../helper/sort.js';
import { CAP, COMP_REPEATER, CONNECTOR, IC, IGNORE, IND, RES, RLC_TYPES, UNUSED } from '../../PCBHelper/index.js';
/**
 * data: {signalPaths, rootPathObj}
 * treeData {root, branch}
 * compMap: Map(name, comp)
 */
const NODE = "node";
class CalculatePathPosition {
  constructor({ data, treeData, compMap, powerNets, signals, visibleSignals }) {
    this.data = data;
    this.treeData = treeData;
    this.compMap = compMap;
    this.powerNets = powerNets;
    this.signals = signals;
    this.layoutNodes = new Map();
    this.layoutComps = new Map();
    this.nodes = new Map();
    this.layoutRootNodes = {}
    this.layoutPaths = []
    this.signalPinLayer = {};
    this.signalPinNext = {};
    this.config = {
      unitSize: 50,           // Pixels per unit length 
      componentWidth: 4,      // component width (unit)
      pinHeight: 1,           // pin height (unit)
      minComponentHeight: 2,  // minimum component height (unit)
      verticalSpacing: 2,     // vertical spacing (unit)
      horizontalSpacing: 4,   // horizontal spacing (unit)
      layerSpacing: 2,    // layer spacing (unit)
      maxUnitLength: 20
    };
    this.parallelRLCs = [];
    this.seriesRLCs = [];
    this.ICRepComps = [];
    this.parallelComps = [];
    this.repeaterComps = [];
    this.parallelAllComps = [];
    this.rootPaths = new Map();
    this.otherPaths = new Map();
    this.nodeSegments = new Map();
    this.startPosY = 0;
    this.signalLayer = {}
    this.visibleSignals = visibleSignals || []
    this.initRLC();
    this.yOffset = 1;
    this.bounds = { width: 500, height: 200 }
    this.netsMaxLength = {};
    this.unusedCompNodes = new Map();
    this.xSpacing = {};
  }

  initRLC = () => {
    //pull up / down res
    this.parallelRLCs = [];
    this.seriesRLCs = [];
    this.ICRepComps = [];
    this.parallelComps = [];
    this.repeaterComps = [];
    for (let comp of this.compMap.values()) {
      if (RLC_TYPES.includes(comp.type) && comp.pins && (comp.pins.find(pin => this.powerNets.includes(pin.net)) || comp.pins.length === 1)) {
        this.parallelRLCs.push(comp.name);
        continue
      }
      if (RLC_TYPES.includes(comp.type) && comp.pins && comp.pins.length === 2 && !comp.pins.find(pin => !pin.signal)) {
        this.seriesRLCs.push(comp.name);
        continue
      }

      if (![COMP_REPEATER, IC, CONNECTOR].includes(comp.type) && comp.pins && (comp.pins.find(pin => this.powerNets.includes(pin.net)) || comp.pins.length === 1)) {
        this.parallelComps.push(comp.name);
        continue;
      }
      if (comp.type === COMP_REPEATER || (RLC_TYPES.includes(comp.type) && comp.pins && comp.pins.length > 2 && !comp.pins.find(pin => !pin.signal))) {
        this.repeaterComps.push(comp.name);
      }
      this.ICRepComps.push(comp.name)
    }
    this.parallelAllComps = [...this.parallelRLCs, ...this.parallelComps];
  }

  //get the length of connection between two nodes
  getSegment(signal, pathName, fromNode, toNode) {
    const key = `${signal}::${pathName}::${fromNode}::${toNode}`;
    return this.nodeSegments.get(key);
  }

  // calculate
  calculatePath = () => {
    this.existPathNames = []; this.top = 0;

    this.startPosY = 1;
    this.rootIndex = 0;
    for (let root of this.treeData.root || []) {
      const rootPath = this.data.signalPaths.find(item => item.signal === this.data.rootPathObj[root].signal &&
        item.name === this.data.rootPathObj[root].name &&
        item.root === root);
      if (!rootPath) {
        continue;
      }
      this.rootIndex += 1;
      this.nodeSegments = this.data.nodeSegments[root] || new Map();
      this.layoutNodes = new Map()
      this.rootPaths.set(root, rootPath);
      this.signalLayer[root] = new Map();
      this.signalLayer[root].set(rootPath.signal, 1);
      this.signalPinLayer[root] = new Map();
      this.signalPinNext[root] = 0;

      let signalPaths = this.getAllOtherPaths(rootPath, root);
      const currRootSignals = signalPaths.map(it => it.signal);

      const signals = this.signals.filter(item => item.name !== rootPath.signal && currRootSignals.includes(item.name)).map(item => item.name);
      /*  let index = 2;
       for (let signal of signals) {
         this.signalLayer[root].set(signal, index);
         index += 1;
       } */

      this.groupLength = this.data.groupLayoutLength[root] || new Map();
      this.nodeLength = this.data.nodeLength[root] || new Map();
      this.parallelPrvComps = this.data.parallelPrvComps[root] || [];
      this.endParallelComps = this.data.endParallelComps[root] || {};
      this.splitChipComps = this.data.splitChipComps[root] || [];
      this.chipCompsPrevList = this.data.chipCompsPrevList[root] || new Map();
      this.sameSegments = this.data.sameSegments[root] || new Map();
      this.sameSegmentsList = [...this.sameSegments.values()].flat(2);
      this.endCompPinList = this.data.endCompPinList[root] || new Map();
      this.startCompPinList = this.data.startCompPinList[root] || new Map();

      signalPaths = SortFn(signalPaths, [rootPath.signal, ...signals], "signal");
      this.allPathNodes = {}
      this.signalGroupPaths = [rootPath, ...signalPaths].map(item => {
        if (!this.allPathNodes[item.signal]) {
          this.allPathNodes[item.signal] = []
        }
        this.allPathNodes[item.signal].push(item.nodes);
        return item.groupPath.map(groupItem => ({ signal: item.signal, name: item.name, groupPath: groupItem, pathNodes: [...item.nodes] }))
      }).flat(2);
      this.allSegments = [...this.nodeSegments.values()];
      this.existSegments = [];
      this.existGroupPaths = [];
      this.xSpacing[root] = new Map();
      this.rootPathNode = [rootPath.groupPath[0][0], rootPath.groupPath[0][rootPath.groupPath[0].length - 1]]
      this.layoutRootPath(root, rootPath)
      this.layoutAllOtherPaths(root, rootPath)
      this.calculatePaths(root);
      this.layoutRootNodes[root] = simpleDeepCopyMap(this.layoutNodes);
      function simpleDeepCopyMap(originalMap) {
        const newMap = new Map();

        for (let [key, value] of originalMap) {
          const copiedValue = JSON.parse(JSON.stringify(value));
          newMap.set(key, copiedValue);
        }

        return newMap;
      }
      this.startPosY += 1;
    }
    this.filterNodes()
    this.calculateBounds();
  }


  filterNodes = () => {
    this.layoutComps = new Map();
    this.nodes = new Map();
    let existComps = {}
    for (let root of this.treeData.root || []) {
      for (let node of this.layoutRootNodes[root].keys()) {
        let nodeKey = node;
        if (this.layoutComps.has(node)) {
          const existCompIndex = existComps[node] ? (existComps[node].length + 1) || 1 : 1;
          nodeKey = `${nodeKey}::${existCompIndex}`;
          existComps[node] = existComps[node] || [];
          existComps[node].push(nodeKey);
        }
        if (this.compMap.has(node)) {
          this.layoutComps.set(nodeKey, this.layoutRootNodes[root].get(node));
        } else {
          this.nodes.set(node, this.layoutRootNodes[root].get(node));
        }
      }
    }
  }

  //layout longest root path (horizontally)
  layoutRootPath(root, path) {
    const { componentWidth } = this.config;
    this.startPosX = 1;

    const findParallelPrvComp = path.nodes.find(item => this.parallelPrvComps.includes(item))
      || path.groupPath.find(pathItem => this.signalGroupPaths.filter(item =>
        item.signal !== path.signal
        && item.groupPath && item.groupPath[0] === pathItem[0]
        && item.groupPath[item.groupPath.length - 1] === pathItem[pathItem.length - 1]
        && item.pathNodes.find(node => this.parallelPrvComps.includes(node))));

    if (findParallelPrvComp) {
      this.startPosY += 2;
    }

    let maxHeight = 2;
    this.lengthNum = 1;// * lengthNum (Prevent node and component from overlapping due to parallel resistors)
    this.compLayer = new Map();
    this.compLayerSpacing = new Map();
    this.parallelCompLayer = new Map();
    this.endParallelCompPrevPins = new Map()
    let signalLayer = this.signalLayer[root].get(path.signal);
    for (let pathItem of path.groupPath) {
      //[U1, A1,U77]
      const groupStart = pathItem[0], groupEnd = pathItem[pathItem.length - 1];
      if (this.splitChipComps.includes(groupStart)) {
        continue;
      }

      const segment = this.getSegment(path.signal, path.name, pathItem[pathItem.length - 2], groupEnd);
      const startNode = this.layoutNodes.get(groupStart);
      if (startNode && this.repeaterComps.includes(groupStart)) {
        const findPin = startNode.pins.find(pin => pin.pin === segment.startPin);
        if (findPin && findPin.x && findPin.x === startNode.x) {
          continue;
        }
      }

      let layerY = this.startPosY;
      this.compLayer.set(`${groupStart}::${groupEnd}`, this.startPosY);
      this.compLayerSpacing.set(`${groupStart}::${groupEnd}`, this.startPosY);

      const groupTotalLength = this.groupLength.get(`${groupStart}::${groupEnd}`);

      let nextNode = null;
      if (!this.ICRepComps.includes(groupEnd)) {
        const findEnd = pathItem[pathItem.length - 2];

        for (let nodes of this.allPathNodes[path.signal] || []) {
          const endIndex = nodes.indexOf(findEnd)
          if (endIndex < 0) {
            continue;
          }
          const nextNodes = nodes.filter((it, index) => index >= endIndex);
          nextNode = nextNodes.find(node => this.ICRepComps.includes(node));
          if (nextNode) {
            break;
          }
        }
        if (nextNode) {
          this.compLayer.set(`${groupStart}::${nextNode}`, layerY);
          this.compLayerSpacing.set(`${groupStart}::${groupEnd}`, layerY);
        }
        this.compLayer.set(`${groupStart}::${groupEnd}`, layerY);
        this.compLayerSpacing.set(`${groupStart}::${groupEnd}`, layerY);
      }

      for (let i = 0; i < pathItem.length - 1; i++) {
        const start = pathItem[i];
        const end = pathItem[i + 1];
        const segment = this.getSegment(path.signal, path.name, start, end);
        //{from, to ,startPin, endPin, length ,net, signal}
        if (!segment) {
          continue;
        }
        let startPin = segment.startPin,
          endPin = segment.endPin;

        let startNode = this.layoutNodes.get(start),//{type, height, pins,width}
          startNodeInfo = this.getNodeInfo(start),
          endNodeInfo = this.getNodeInfo(end);//{type, height, pins 
        //update start component and pins 
        if (startNodeInfo.type !== NODE && !this.parallelAllComps.includes(start)) {
          //the first node
          let x = startNode ? startNode.x : this.startPosX,
            y = layerY;

          const pins = startNode && startNode.pins ? startNode.pins : startNodeInfo.pins || [];
          const index = pins.findIndex(pin => pin.pin === startPin);
          if (index > -1) {

            if (this.seriesRLCs.includes(start) && startNode && pins[index].x === startNode.x) {
              startNode.isY = true;
            } else if (!(this.parallelAllComps.includes(end) && pins[index].x)) {
              const width = startNode ? startNode.width : componentWidth;
              const findPins = this.endCompPinList.get(start);
              const isEndPin = findPins && findPins.includes(startPin) && start !== root;
              const findSegment = isEndPin ? this.allSegments.find(item => item.from !== start && item.to === start && item.endPin === startPin) : null;
              pins[index].x = isEndPin && findSegment ? x : x + width;
              pins[index].y = this.seriesRLCs.includes(start) ? (startNode ? startNode.y : y) + 0.23 : y + signalLayer;
            }
          }

          const findSamePinSegment = this.ICRepComps.includes(start) ? this.getSegment(path.signal, path.name, start, start) : null;
          const newIndex = findSamePinSegment ? pins.findIndex(pin => pin.pin === findSamePinSegment.startPin) : -1;
          if (newIndex > -1 && !pins[newIndex].x) {
            pins[newIndex].x = x + componentWidth - 1;
            pins[newIndex].y = y + signalLayer;
          }
          if (startNode) {
            this.layoutNodes.set(start, {
              ...startNode,
              pins
            });
          } else {

            this.layoutNodes.set(start, {
              x,
              y,
              width: componentWidth,
              height: startNodeInfo.height,
              type: startNodeInfo.type,
              pins
            });
            if (maxHeight < startNodeInfo.height) {
              maxHeight = startNodeInfo.height;
            }
          }
        }

        //update end component and pins or node
        const groupStartNode = this.layoutNodes.get(groupStart) || {};
        const nodeLength = this.nodeLength.get(end) || groupTotalLength || 0;
        //end node is groupTotalLength

        let x = groupStartNode.x + groupStartNode.width + nodeLength,
          y = endNodeInfo.type === NODE ? layerY + 1 : layerY,
          compWidth = componentWidth,
          compHeight = endNodeInfo.height;
        const pins = endNodeInfo.pins || [];
        if (this.parallelAllComps.includes(end)) {
          //pull up /down res
          y = layerY - 2;
          compWidth = 0.46;
          compHeight = 1;
          if (this.ICRepComps.includes(start)) {
            const findPins = this.startCompPinList.get(start);
            const isEndPin = findPins && findPins.includes(startPin) && start !== root;
            const findSegment = isEndPin ? this.allSegments.find(item => item.from !== start && item.to === start && item.endPin === startPin) : null;
            x = isEndPin && findSegment ? startNode.x : x - 0.23;
          }
          let pins = this.endParallelCompPrevPins.get(start) || [];
          pins = [...new Set(pins.concat(startPin))];
          this.endParallelCompPrevPins.set(start, pins);
        } else if (this.seriesRLCs.includes(end)) {
          //series rlc
          y = layerY + 0.77;
          compWidth = 2;
          compHeight = 0.46;
          const index = pins.findIndex(pin => pin.pin === endPin);
          if (index > -1) {
            pins[index].x = x;
            pins[index].y = y + 0.23;
          }
        } else {
          const index = pins.findIndex(pin => pin.pin === endPin);
          if (index > -1) {
            pins[index].x = x;
            pins[index].y = y + 1;
          }
          const findSamePinSegment = this.getSegment(path.signal, path.name, end, end);
          const newIndex = findSamePinSegment ? pins.findIndex(pin => pin.pin === findSamePinSegment.endPin) : -1;
          if (newIndex > -1 && !pins[newIndex].x) {
            pins[newIndex].x = x + 1.5;
            pins[newIndex].y = y + 1;
          }

          //find pins connected same from node
          const filterSegments = this.sameSegments.get(`${path.signal}::${start}::${end}`) || [];
          //find pins connected same from node
          if (filterSegments.length > 0) {
            for (let segItem of filterSegments) {
              const index = pins.findIndex(pin => pin.pin === segItem.endPin);
              if (index > -1 && (!pins[index].x || !pins[index].y)) {
                pins[index].x = x;
                const segPinKey = `${end}::${segItem.endPin}`;
                const segPinLayer = this.shouldAllocateSlotForPin(root, path.signal, end, segItem.endPin, endNodeInfo.type, (end === root), (end === groupEnd))
                  ? this.getAndSetSignalLayer(root, path.signal, segPinKey)
                  : (this.signalLayer[root] ? this.signalLayer[root].get(path.signal) || 1 : 1);
                pins[index].y = y + segPinLayer /* + 0.5 */;
              }
            }
          }
        }
        this.layoutNodes.set(end, {
          x,
          y,
          width: compWidth,
          height: compHeight,
          type: endNodeInfo.type,
          pins,
          signal: endNodeInfo.type === NODE ? path.signal : '',
        });
        if (maxHeight < compHeight) {
          maxHeight = compHeight;
        }

        this.existSegments.push(`${path.signal}::${path.name}::${start}::${end}`);
      }
      //set other signals for same group
      this.existGroupPaths.push(`${path.signal}::${path.name}::${groupStart}::${groupEnd}`);

      const filterGroups = this.signalGroupPaths.filter(item =>
       /*  item.signal !== path.signal
        && */ item.groupPath && item.groupPath[0] === groupStart
        && item.groupPath[item.groupPath.length - 1] === groupEnd
        && !this.existGroupPaths.includes(`${item.signal}::${item.name}::${item.groupPath[0]}::${item.groupPath[item.groupPath.length - 1]}`));

      this.layoutSinglePath({ filterGroups, root, isRoot: true });

      let groupEndNode = this.ICRepComps.includes(groupEnd) ? this.layoutNodes.get(groupEnd) : this.layoutNodes.get(nextNode);
      if (!groupEndNode) {
        groupEndNode = this.layoutNodes.get(groupEnd)
      }
      this.startPosX = groupEndNode && groupEndNode.type !== NODE ? groupEndNode.x + groupEndNode.width : groupEndNode.x;
    }
    this.startPosY = this.startPosY + maxHeight;
    //filter parallel groups and end node not IC
    const groupStart = path.groupPath[0][0], groupEnd = path.groupPath[0][path.groupPath[0].length - 1];

    const sameStartParallelGroups = this.ICRepComps.includes(groupEnd) ? this.signalGroupPaths.filter(item => {
      if (item.groupPath && item.groupPath[0] === groupStart
        && item.groupPath[item.groupPath.length - 1] !== groupEnd
        && !this.ICRepComps.includes(item.groupPath[item.groupPath.length - 1])
        && item.groupPath[item.groupPath.length - 2] && !this.ICRepComps.includes(item.groupPath[item.groupPath.length - 2])
        && !this.existGroupPaths.includes(`${item.signal}::${item.name}::${item.groupPath[0]}::${item.groupPath[item.groupPath.length - 1]}`)) {
        const endNode = item.groupPath[item.groupPath.length - 2]
        const findEndNodeGroups = (this.allPathNodes[item.signal] || []).find(nodes => {
          const index = nodes.indexOf(endNode);
          if (index < 0) {
            return false;
          }
          const findIc = nodes.find((node, _index) => index <= _index && this.ICRepComps.includes(node));
          return findIc ? true : false;
        })
        if (!findEndNodeGroups) {
          return true;
        }
      }
      return false;
    }) : [];

    if (sameStartParallelGroups.length) {
      this.layoutSinglePath({ filterGroups: sameStartParallelGroups, root, isParallelGroup: true });
    }
  }

  getAndSetSignalLayer = (root, signal, compOrPin) => {
    // Returns a numeric layer for a signal; if compOrPin provided (e.g. "COMP::PIN"),
    // allocate a stable per-(signal,compOrPin) slot so the same pin gets the same slot.
    let layer = 1;
    /*  const group = this.signalLayer[root].get(groupName);
     if (!group) {
       let signalMap = new Map();
       signalMap.set(signal, 1);
       this.signalLayer[root].set(group, signalMap);
       layer = 1;
     } else {
       const signalLayer = group.get(signal);
       const length = Array.from(group.values()).length;
       if (!signalLayer) {
         group.set(signal, length + 1);
         layer = length + 1;
       } else {
         layer = signalLayer
       }
     } */
    if (!this.signalLayer[root]) this.signalLayer[root] = new Map();
    if (!this.signalPinLayer[root]) this.signalPinLayer[root] = new Map();
    if (this.signalPinNext[root] === undefined) this.signalPinNext[root] = 0;

    const signalLayer = this.signalLayer[root].get(signal);
    if (!signalLayer) {
      const length = Array.from(this.signalLayer[root].values()).length;
      this.signalLayer[root].set(signal, length + 1);
      layer = length + 1;
    } else {
      layer = signalLayer
    }

    // If no comp/pin specified, return base layer
    if (!compOrPin) return layer;

    const key = `${compOrPin}`;
    if (this.signalPinLayer[root].has(key)) {
      return /* layer + */ this.signalPinLayer[root].get(key);
    }

    // Allocate slots for all pins of this component for the given signal
    // in a deterministic order. Prefer sorting by pin.y when available,
    // otherwise sort by pin name. This keeps per-pin layer ordering
    // consistent with component pin positions (helps visual ordering).
    const [compName, pinName] = compOrPin.split('::');
    const comp = this.compMap ? this.compMap.get(compName) : null;
    let pinsToAllocate = [];
    if (comp && comp.pins) {
      pinsToAllocate = comp.pins/* .filter(p => p.signal === signal) */.map(p => p.pin);
      pinsToAllocate = [...new Set(pinsToAllocate)];
      pinsToAllocate.sort((a, b) => {
        const pa = comp.pins.find(p => p.pin === a) || {};
        const pb = comp.pins.find(p => p.pin === b) || {};
        if (pa.y !== undefined && pb.y !== undefined) return pa.y - pb.y;
        return String(a).localeCompare(String(b));
      });
    } else {
      // fallback: only allocate the requested pin
      pinsToAllocate = [pinName];
    }

    const node = this.layoutNodes.get(compName) || {};
    const startCompPins = this.startCompPinList.get(compName) || [],
      endCompPins = this.endCompPinList.get(compName) || [];
    const side = endCompPins.includes(pinName) ? 'end' : (startCompPins.includes(pinName) ? 'start' : '');
    let sidePins = [], isNewPinLayer = false;
    for (let pin of node.pins || []) {
      const itemSide = endCompPins.includes(pin.pin) ? 'end' : (startCompPins.includes(pin.pin) ? 'start' : '');
      if (itemSide === side && side) {
        sidePins.push(pin);
        if (pin.x && pin.y && pin.signal === signal) {
          isNewPinLayer = true;
        }
      }
    }
    if (isNewPinLayer && sidePins.length) {
      const allSignals = this.visibleSignals.length ? [...this.visibleSignals] : this.signals.map(item => item.name);
      const minLayer = allSignals.length;
      const maxPinLayer = sidePins.reduce((max, pin) => {
        const k = `${compName}::${pin.pin}`;
        if (this.signalPinLayer[root] && this.signalPinLayer[root].has(k)) {
          // already allocated per-pin slot — use it (getAndSet won't allocate since exists)
          return Math.max(max, this.signalPinLayer[root].get(k));
        }
        // fallback to the base signal layer without allocating new per-pin slots
        return Math.max(max, minLayer);
      }, 1);
      this.signalPinLayer[root].set(key, maxPinLayer + 1);
    } else {
      this.signalPinLayer[root].set(key, layer);
    }

    return this.signalPinLayer[root].get(key);
  }

  // Determine whether we should allocate a dedicated slot for a given pin.
  // Only allocate when the node type is IC/CONNECTOR/UNUSED AND the node is
  // either the root node or the final node for the current group, and the
  // component has more than one pin for the same signal.
  shouldAllocateSlotForPin = (root, signal, node, pin, nodeType) => {
    if (!pin) return false;
    // count pins for this node and signal
    const nodeInfo = this.getNodeInfo(node) || { pins: [] };
    const pins = nodeInfo.pins || [];

    const rightoverPins = this.startCompPinList.get(node) || [];
    const leftoverPins = this.endCompPinList.get(node) || [];

    const rightSlidePins = pins.filter(p => p.signal === signal && rightoverPins.includes(p.pin));
    const leftSlidePins = pins.filter(p => p.signal === signal && leftoverPins.includes(p.pin));
    return (rightoverPins.includes(pin) && rightSlidePins.length > 1) || (leftoverPins.includes(pin) && leftSlidePins.length > 1);

  }

  // layout other paths relative to the root path
  layoutAllOtherPaths = (root, rootPath) => {
    // assign a layer for each path
    const rootGroupNodes = [...rootPath.groupNodes].reverse();
    for (let rootGroupNode of rootGroupNodes) {
      if (!this.layoutNodes.has(rootGroupNode)) {
        continue;
      }
      //filter same segments
      const filterGroups = this.signalGroupPaths.filter(item =>
        item.groupPath && item.groupPath[0] === rootGroupNode
        && !this.existGroupPaths.includes(`${item.signal}::${item.name}::${item.groupPath[0]}::${item.groupPath[item.groupPath.length - 1]}`));
      this.layoutGroupPath({ filterGroups, root });
    }
  }

  layoutGroupPath = ({ filterGroups, root }) => {
    const newGroups = this.layoutSinglePath({ filterGroups, root });
    if (newGroups && newGroups.length) {
      this.layoutGroupPath({ filterGroups: newGroups, root });
    }
  }

  layoutSinglePath = ({ filterGroups, root, isRoot = false, isParallelGroup = false, isSameGroup = false }) => {
    const { componentWidth } = this.config;

    let newFilterGroups = JSON.parse(JSON.stringify(filterGroups)).sort((a, b) =>
      (this.ICRepComps.includes(b.groupPath[b.groupPath.length - 1]) ? 1 : 0) - (this.ICRepComps.includes(a.groupPath[a.groupPath.length - 1]) ? 1 : 0));
    newFilterGroups = newFilterGroups.sort((a, b) =>
      (this.layoutNodes.has(b.groupPath[b.groupPath.length - 1]) ? 1 : 0) - (this.layoutNodes.has(a.groupPath[a.groupPath.length - 1]) ? 1 : 0));

    newFilterGroups = newFilterGroups.sort((a, b) => {
      return ((this.layoutNodes.get(a.groupPath[a.groupPath.length - 1]) || {}).y || Infinity)
        - ((this.layoutNodes.get(b.groupPath[b.groupPath.length - 1]) || {}).y || Infinity)
    })

    for (let filterGroup of newFilterGroups) {
      const { name, signal, groupPath, pathNodes } = filterGroup;

      const groupStart = groupPath[0], groupEnd = groupPath[groupPath.length - 1];
      const groupToNodeInfo = this.getNodeInfo(groupEnd);
      const groupFromNodeInfo = this.getNodeInfo(groupStart);
      const endSegment = this.nodeSegments.get(`${signal}::${name}::${groupPath[groupPath.length - 2]}::${groupEnd}`);
      const startSegment = this.nodeSegments.get(`${signal}::${name}::${groupStart}::${groupPath[1]}`);

      // let signalLayer = this.signalLayer[root].get(signal)
      let signalLayer = this.getAndSetSignalLayer(root, signal);
      let fromSignalLayer = signalLayer;
      let baseSignalLayer = this.signalLayer[root] ? this.signalLayer[root].get(signal) || 1 : 1;
      const groupTotalLength = this.groupLength.get(`${groupStart}::${groupEnd}`);
      const findParallel = [...groupPath].find(item => !this.layoutNodes.has(item) && (this.parallelPrvComps.includes(item) || this.parallelAllComps.includes(item)));

      let compYLayer = this.compLayer.get(`${groupStart}::${groupEnd}`);
      let isNewParallelLayer = false, isExistEndParallelY = false;
      if (!this.ICRepComps.includes(groupEnd)) {
        const findEnd = groupPath[groupPath.length - 2];

        let nextNode = null, nextLayer = 0;/* , nextList = [] */;
        if (!this.parallelAllComps.includes(findEnd)) {
          for (let nodes of this.allPathNodes[signal] || []) {
            const endIndex = nodes.indexOf(findEnd)
            if (endIndex < 0) {
              continue;
            }
            const nextNodes = nodes.filter((it, index) => index >= endIndex);
            let _nextNode = nextNodes.find(node => this.ICRepComps.includes(node));
            let _nextLayer = 0;
            if (_nextNode) {
              const currSeg = this.getSegment(signal, name, findEnd, groupEnd);
              const findLayer = this.compLayer.get(`${groupStart}::${_nextNode}`);
              const findLayerSpacing = this.compLayerSpacing.get(`${groupStart}::${_nextNode}`);
              const parallelCompLayer = this.parallelCompLayer.get(`${findEnd}::${_nextNode}`);
              _nextLayer = findLayer || 0;

              //filter without parallel resistors path layer
              if (parallelCompLayer || (findLayer && findLayerSpacing < 3)) {
                _nextNode = null;
                _nextLayer = 0;
              }

              const nextNodeSegments = this.allSegments.filter(item => item.signal === signal && item.from === findEnd && item.to === nextNodes[1]);
              if (_nextNode && currSeg && currSeg.from && nextNodeSegments.length && nextNodeSegments.find(item => item.startPin !== currSeg.startPin)) {
                _nextNode = null;
                _nextLayer = 0;
              }
            }
            if (_nextNode && nextLayer < _nextLayer) {
              nextNode = _nextNode;
              nextLayer = _nextLayer;
              break;
            }
          }
        }

      /*   nextList = nextList.filter(item => item !== groupEnd);
        const findParallelComp = nextList.find(item => this.parallelAllComps.includes(item));
        if (findParallelComp) {
          isNewParallelLayer = true
        } else */ if (nextNode) {
          compYLayer = this.compLayer.get(`${groupStart}::${nextNode}`);
          this.parallelCompLayer.set(`${findEnd}::${nextNode}`, true);
        } else {
          isNewParallelLayer = true
        };

      } else if (!compYLayer && this.layoutNodes.has(groupEnd)) {
        compYLayer = this.layoutNodes.get(groupEnd).y;
      }
      if (isNewParallelLayer) {
        signalLayer = 1;
        baseSignalLayer = 1;
      }
      // const findStartCompLayer = [...this.compLayer.keys()].find(key => key.match(`${groupStart}::`));
      const isNewLayer = !compYLayer /* && findStartCompLayer */;
      let ySpacing = 0
      if (isNewLayer) {
        const _findParallel = findParallel || pathNodes.find(item => !this.layoutNodes.has(item) && (this.parallelPrvComps.includes(item) || this.parallelAllComps.includes(item)))
          || this.signalGroupPaths.find(item =>
            item.name !== name
            && item.groupPath && item.groupPath[0] === groupStart
            && item.groupPath[item.groupPath.length - 1] === groupEnd
            && item.pathNodes.find(node => !this.layoutNodes.has(node) && (this.parallelPrvComps.includes(node) || this.parallelAllComps.includes(node))));
        ySpacing = _findParallel ? 3 : 1;
      }
      const newY = this.startPosY + ySpacing;
      let layerY = isNewLayer ? newY : compYLayer;
      let isNewEnd = false;
      if (!isNewLayer && this.ICRepComps.includes(groupEnd) && endSegment) {
        const pinKey = `${groupEnd}::${endSegment.endPin}`;
        if (this.shouldAllocateSlotForPin(root, signal, groupEnd, endSegment.endPin, groupToNodeInfo.type)) {
          let pinLayer = this.getAndSetSignalLayer(root, signal, pinKey);
          signalLayer = pinLayer;
          baseSignalLayer = pinLayer;
          fromSignalLayer = pinLayer;
          isNewEnd = true;
        }
      }
      if (!isNewEnd && !isNewLayer && this.ICRepComps.includes(groupStart) && startSegment) {
        const pinKey = `${groupStart}::${startSegment.startPin}`;
        if (this.shouldAllocateSlotForPin(root, signal, groupStart, startSegment.startPin, groupFromNodeInfo.type)) {
          let pinLayer = this.getAndSetSignalLayer(root, signal, pinKey);
          signalLayer = pinLayer;
          baseSignalLayer = pinLayer;
          fromSignalLayer = pinLayer;
        }
      }

      let groupXSpacing = this.rootPathNode.includes(groupStart) && this.rootPathNode.includes(groupEnd) ? 0 : 1;

      let maxHeight = 0;

      for (let i = 0; i < groupPath.length - 1; i++) {
        const from = groupPath[i], to = groupPath[i + 1];
        const segment = this.nodeSegments.get(`${signal}::${name}::${from}::${to}`);
        if (!segment) continue;
        // if (this.existSegments.includes(`${signal}::${from}::${to}`)) continue;
        const isExist = this.existSegments.includes(`${signal}::${name}::${from}::${to}`);
        //get node info
        let fromNode = this.layoutNodes.get(from) || {},
          toNode = this.layoutNodes.get(to),
          /* fromNodeInfo = this.getNodeInfo(from), */
          toNodeInfo = this.getNodeInfo(to);
        //update from component pin node
        if (fromNode.type !== NODE && !isExist) {
          //from node update (comp pin)
          let fromY = layerY;
          this.updateStartNodeToLayoutNodes({
            node: from,
            layoutNode: fromNode,
            startPin: segment.startPin,
            to,
            fromY,
            signal,
            groupEnd,
            root,
            isNewParallelLayer,
            name,
            fromSignalLayer
          });

          fromNode = this.layoutNodes.get(from);
        }

        //update end component and pins or node
        const groupStartNode = this.layoutNodes.get(groupStart) || {};
        const nodeLength = this.nodeLength.get(to) || groupTotalLength;
        let toX = (groupStartNode.x || 0) + (groupStartNode.width || 0) + groupXSpacing + nodeLength,
          toY = toNodeInfo.type === NODE ? layerY + signalLayer : layerY,
          compWidth = componentWidth,
          compHeight = toNode ? toNode.height : toNodeInfo.height;
        //parallel rlc
        if (this.parallelAllComps.includes(to)) {

          if (!toNode) {
            const info = this.setParallelRLCToLayoutNodes({ segment, from, to, fromNode, toNodeInfo, signalLayer, toX, toY, groupEnd, isNewParallelLayer, root, groupStartNode, isNewLayer });
            isNewParallelLayer = info.isNewParallelLayer;
            isExistEndParallelY = info.isExistEndParallelY;
          } else if (groupEnd !== to && toNode.y > toY - 2) {
            toNode.y = toY - 2;
            this.layoutNodes.set(to, {
              ...toNode
            });
          }
          !isExist && this.existSegments.push(`${signal}::${name}::${from}::${to}`);
          if (maxHeight < 1) {
            maxHeight = 1;
          }
          continue;
        }

        //to node 
        if (toNodeInfo.type === NODE) {

          if (!toNode) {
            this.layoutNodes.set(to, {
              x: toX,
              y: toY,
              width: 0.5,
              height: 1,
              type: toNodeInfo.type,
              pins: [],
              signal
            });
            this.existSegments.push(`${signal}::${name}::${from}::${to}`);
            if (maxHeight < 1) {
              maxHeight = 1;
            }
          } else if (toNode.y > toY) {
            toNode.y = toY;
            this.layoutNodes.set(to, {
              ...toNode
            });
          }
          continue;
        }

        //repeater, ic

        if ([IC, CONNECTOR, UNUSED, RES, IND, CAP, COMP_REPEATER, IGNORE].includes(toNodeInfo.type) && !isExist) {
          let pins = toNodeInfo.pins || [];

          if (!toNode) {
            if (this.seriesRLCs.includes(to)) {
              toY = toY + baseSignalLayer - 0.23;
              compWidth = 2;
              compHeight = 0.46;
            }
          } else {
            toX = toNode.x;
            toY = toNode.y;
            pins = toNode.pins || toNodeInfo.pins || [];
          }

          const index = pins.findIndex(pin => pin.pin === segment.endPin);
          const toNodeType = toNodeInfo.type;
          const isRootNode = to === root;
          const isFinalNode = to === groupEnd;
          const baseLayer = this.signalLayer[root] ? this.signalLayer[root].get(signal) || 1 : 1;
          if (index > -1 && (!pins[index].x || !pins[index].y)) {
            pins[index].x = toX;
            const pinKey = `${to}::${segment.endPin}`;
            const pinLayer = this.shouldAllocateSlotForPin(root, signal, to, segment.endPin, toNodeType, isRootNode, isFinalNode)
              ? this.getAndSetSignalLayer(root, signal, pinKey)
              : baseLayer;

            pins[index].y = this.seriesRLCs.includes(to) ? toY + 0.23 : toY + pinLayer;
          }

          if (index > -1 && !this.seriesRLCs.includes(to)) {
            const findSamePinSegment = this.getSegment(signal, name, to, to);
            const newIndex = findSamePinSegment ? pins.findIndex(pin => pin.pin === findSamePinSegment.endPin) : -1;
            if (newIndex > -1 && !pins[newIndex].x) {
              pins[newIndex].x = toX + 1.5;
              const otherPinKey = `${to}::${findSamePinSegment.endPin}`;
              const otherPinLayer = this.shouldAllocateSlotForPin(root, signal, to, findSamePinSegment.endPin, toNodeType, isRootNode, isFinalNode)
                ? this.getAndSetSignalLayer(root, signal, otherPinKey)
                : baseLayer;
              pins[newIndex].y = toY + otherPinLayer;
            }
          }
          //find pins connected same from node
          const filterSegments = this.sameSegments.get(`${signal}::${from}::${to}`) || [];
          if (filterSegments.length > 0) {
            for (let segItem of filterSegments) {
              const index = pins.findIndex(pin => pin.pin === segItem.endPin);
              if (index > -1 && (!pins[index].x || !pins[index].y)) {
                pins[index].x = toX;
                const segPinKey = `${to}::${segItem.endPin}`;
                const segPinLayer = this.shouldAllocateSlotForPin(root, signal, to, segItem.endPin, toNodeInfo.type, (to === root), (to === groupEnd))
                  ? this.getAndSetSignalLayer(root, signal, segPinKey)
                  : (this.signalLayer[root] ? this.signalLayer[root].get(signal) || 1 : 1);
                pins[index].y = toY + segPinLayer /* + 0.5 */;
              }
            }
          }
          if (!this.seriesRLCs.includes(to)) {
            const maxSignalLayer = pins.reduce((max, pin) => {
              if (!pin.x) return max;
              const key = `${to}::${pin.pin}`;
              if (this.signalPinLayer[root] && this.signalPinLayer[root].has(key)) {
                // already allocated per-pin slot — use it (getAndSet won't allocate since exists)
                return Math.max(max, this.getAndSetSignalLayer(root, pin.signal, `${to}::${pin.pin}`) || 1);
              }
              // fallback to the base signal layer without allocating new per-pin slots
              return Math.max(max, (this.signalLayer[root] ? this.signalLayer[root].get(pin.signal) || 1 : 1));
            }, 1);
            compHeight = compHeight >= maxSignalLayer + 1 ? compHeight : maxSignalLayer + 1;
          }

          if (!toNode) {
            this.layoutNodes.set(to, {
              x: toX,
              y: toY,
              width: compWidth,
              height: compHeight,
              type: toNodeInfo.type,
              pins
            });
          } else {
            if (compHeight > toNode.height) {
              this.startPosY += compHeight - toNode.height;
            }
            this.layoutNodes.set(to, {
              ...toNode,
              pins,
              height: compHeight
            });

          }
          if (maxHeight < compHeight) {
            maxHeight = compHeight;
          }
        }

        !isExist && this.existSegments.push(`${signal}::${name}::${from}::${to}`);
      }
      this.existGroupPaths.push(`${signal}::${name}::${groupStart}::${groupEnd}`);

      this.compLayer.set(`${groupStart}::${groupEnd}`, layerY);
      if (isNewLayer) {
        this.compLayerSpacing.set(`${groupStart}::${groupEnd}`, ySpacing);
      }

      if (isNewLayer && !isExistEndParallelY && (this.ICRepComps.includes(groupEnd) || isNewParallelLayer)) {
        this.startPosY = this.startPosY + ySpacing + maxHeight;
      }
      //set other signals for same group
      const sameGroups = !isRoot && !isParallelGroup ? this.signalGroupPaths.filter(item =>
        /* item.signal !== signal
        &&  */item.groupPath && item.groupPath[0] === groupStart
        && item.groupPath[item.groupPath.length - 1] === groupEnd
        && !this.existGroupPaths.includes(`${item.signal}::${item.name}::${item.groupPath[0]}::${item.groupPath[item.groupPath.length - 1]}`)) : [];
      if (sameGroups.length > 0) {
        this.layoutSinglePath({ filterGroups: sameGroups, root, isSameGroup: true })
      }

      //filter parallel groups and end node not IC
      const sameStartParallelGroups = !isRoot && !isSameGroup && this.ICRepComps.includes(groupEnd) ? this.signalGroupPaths.filter(item => {
        if (item.groupPath && item.groupPath[0] === groupStart
          && item.groupPath[item.groupPath.length - 1] !== groupEnd
          && !this.ICRepComps.includes(item.groupPath[item.groupPath.length - 1])
          && item.groupPath[item.groupPath.length - 2] && !this.ICRepComps.includes(item.groupPath[item.groupPath.length - 2])
          && !this.existGroupPaths.includes(`${item.signal}::${item.name}::${item.groupPath[0]}::${item.groupPath[item.groupPath.length - 1]}`)) {
          const endNode = item.groupPath[item.groupPath.length - 2]
          const findEndNodeGroups = (this.allPathNodes[item.signal] || []).find(nodes => {
            const index = nodes.indexOf(endNode);
            if (index < 0) {
              return false;
            }
            const findIc = nodes.find((node, _index) => index <= _index && this.ICRepComps.includes(node));
            return findIc ? true : false;
          })
          if (!findEndNodeGroups) {
            return true;
          }
        }
        return false;
      }) : [];

      if (sameStartParallelGroups.length) {
        this.layoutSinglePath({ filterGroups: sameStartParallelGroups, root, isParallelGroup: true });
      }

      if (this.ICRepComps.includes(groupEnd) && (!isRoot && !isParallelGroup)) {

        /*  newGroups.push(... */
        const newGroups = this.signalGroupPaths.filter(item =>
          item.groupPath && item.groupPath[0] === groupEnd
          && !this.chipCompsPrevList.get(groupEnd).find(prevComp => !this.layoutNodes.has(prevComp))
          && !this.existGroupPaths.includes(`${item.signal}::${item.name}::${item.groupPath[0]}::${item.groupPath[item.groupPath.length - 1]}`))/* ) */;
        newGroups.length && this.layoutSinglePath({ filterGroups: newGroups, root,/*  isSameGroup: true */ })
      }
    }
    /*  return newGroups; */
  }

  updateStartNodeToLayoutNodes = ({ node, layoutNode, isNewParallelLayer, startPin, fromY, signal, name, to, groupEnd, root, fromSignalLayer }) => {
    // signals - from node update pin position
    //comp, update component pin position
    const index = (layoutNode.pins || []).findIndex(it => it.pin === startPin);
    const isSplitChip = this.splitChipComps.includes(node);
    const layoutWidth = isSplitChip ? 0 : layoutNode.width;
    const isIC = this.ICRepComps.includes(node);
    if (index > -1) {
      // determine a stable per-(signal,component::pin) layer so the same pin reuses same slot
      const pinKey = `${node}::${startPin}`;
      const nodeType = layoutNode.type || this.getNodeInfo(node).type;
      const isRootNode = node === root;
      const isFinalNode = node === groupEnd;
      const pinLayer = isNewParallelLayer ? (isIC ? fromSignalLayer : 1)
        : this.shouldAllocateSlotForPin(root, signal, node, startPin, nodeType, isRootNode, isFinalNode)
          ? this.getAndSetSignalLayer(root, signal, pinKey)
          : (this.signalLayer[root] ? this.signalLayer[root].get(signal) || 1 : 1);

      if (layoutNode.isY && !layoutNode.pins[index].x) {
        layoutNode.pins[index].x = layoutNode.x + layoutWidth;
        layoutNode.y = this.seriesRLCs.includes(node) ? fromY + pinLayer - 0.23 : fromY;
        layoutNode.pins[index].y = this.seriesRLCs.includes(node) ? layoutNode.y + 0.23 : layoutNode.y + pinLayer;
      } else if (this.seriesRLCs.includes(node) && layoutNode && !layoutNode.isY && layoutNode.pins[index].x === layoutNode.x) {
        layoutNode.isY = true;
      } else if (!layoutNode.pins[index].x) {
        let width = layoutWidth;
        if (this.parallelAllComps.includes(to)
          && to === groupEnd
          && !this.allSegments.find(it => it.from === node && it.starPin === startPin && it.to !== to && !this.parallelAllComps.includes(it.to))) {
          width = 0;
        }
        const findPins = this.endCompPinList.get(node);
        const isEndPin = findPins && findPins.includes(startPin) && node !== root;
        const findSegment = isEndPin ? this.allSegments.find(item => item.from !== node && item.to === node && item.endPin === startPin) : null;
        layoutNode.pins[index].x = isEndPin && findSegment ? layoutNode.x : layoutNode.x + width;
        layoutNode.pins[index].y = this.seriesRLCs.includes(node) ? layoutNode.y + 0.23 : layoutNode.y + pinLayer;
        if (!this.seriesRLCs.includes(node) && !this.parallelAllComps.includes(node) && pinLayer) {
          layoutNode.height = layoutNode.height >= pinLayer + 1 ? layoutNode.height : pinLayer + 1;
        }
      }

      const findSamePinSegment = this.ICRepComps.includes(node) ? this.getSegment(signal, name, node, node) : null;
      const newIndex = findSamePinSegment ? layoutNode.pins.findIndex(pin => pin.pin === findSamePinSegment.startPin) : -1;
      if (newIndex > -1 && !layoutNode.pins[newIndex].x) {
        layoutNode.pins[newIndex].x = layoutNode.x + layoutNode.width - 1;
        const otherPinKey = `${node}::${findSamePinSegment.startPin}`;
        const otherNodeType = layoutNode.type || this.getNodeInfo(node).type;
        const otherIsRoot = node === root;
        const otherIsFinal = node === groupEnd;
        const otherPinLayer = this.shouldAllocateSlotForPin(root, signal, node, findSamePinSegment.startPin, otherNodeType, otherIsRoot, otherIsFinal)
          ? this.getAndSetSignalLayer(root, signal, otherPinKey)
          : (this.signalLayer[root] ? this.signalLayer[root].get(signal) || 1 : 1);
        layoutNode.pins[newIndex].y = layoutNode.y + otherPinLayer;
      }
      this.layoutNodes.set(node, {
        ...layoutNode
      });
    }
  }

  setParallelRLCToLayoutNodes = ({ segment, from, to, fromNode, toNodeInfo, signalLayer, toX, toY, groupEnd, isNewParallelLayer, root, groupStartNode, isNewLayer }) => {
    const fromPin = segment.startPin;
    let fromX = fromNode.x, fromPinY = null;
    if (groupEnd !== to /* && !this.parallelAllComps.includes(groupEnd) */) {
      this.layoutNodes.set(to, {
        x: toX - 0.23,
        y: toY - 2,
        width: 0.46,
        height: 1,
        type: toNodeInfo.type,
        pins: toNodeInfo.pins,
        isNewParallelLayer
      });
      return { isNewParallelLayer, isExistEndParallelY: false }
    }

    let fromXPosition = fromNode.x, _isExistEndParallelY = true;
    if (fromNode.type !== NODE && !this.parallelAllComps.includes(from)) {
      const fromPinInfo = (fromNode.pins || []).find(pin => pin.pin === fromPin);
      let set = false;
      if (fromPinInfo && fromPinInfo.x && fromPinInfo.x === fromNode.x) {
        fromX = fromPinInfo.x + 0.25;
        fromPinY = fromPinInfo.y;
        set = true;
      } else if (fromPinInfo && fromPinInfo.x && fromPinInfo.x === (fromNode.x + fromNode.width)) {
        fromX = fromPinInfo.x - 0.25;
        set = true;
        fromXPosition = fromPinInfo.x
      }
      if (!set) {
        //this pin has not been set to layoutNode
        const endPin = this.allSegments.find(item => item.to === from && item.endPin === fromPin);
        if (endPin) {
          fromX = fromNode.x + 0.25;
        } else {
          fromX = fromNode.x + fromNode.width - 0.25;
          fromXPosition = fromNode.x + fromNode.width;
        }
      }
    }

    const fromY = fromPinY ? fromPinY - 0.23 : fromNode.y;
    if (fromPinY && this.seriesRLCs.includes(from) && fromNode.isY) {
      fromX = fromX - 0.25;
    }

    let _toX = this.parallelAllComps.includes(from) ? toX - 0.23 : fromX - 0.23,
      _toY = fromNode.type === NODE || this.seriesRLCs.includes(from) ? fromY - (signalLayer - 1) - 3 : fromNode.y - 2;
    if (this.seriesRLCs.includes(from)) {
      _toY += 0.23;
    }
    if (this.parallelAllComps.includes(from)) {
      _toY = fromNode.y;
    }

    let newLayer = false;
    const towParallel = this.parallelAllComps.includes(from) && isNewParallelLayer;
    //find : one pin is connected multiple parallel rlc/diode/tp
    const findAnotherSeg = !towParallel ? [...this.nodeSegments.values()].find(item =>
      item.from === from &&
      item.to !== to &&
      item.startPin === segment.startPin
      && this.parallelAllComps.includes(item.to)
      && this.layoutNodes.has(item.to) && this.layoutNodes.get(item.to).x === _toX) : null;

    if (findAnotherSeg || towParallel) {
      _toY = toY - 2;
      _isExistEndParallelY = false;
      newLayer = true;
      _toX = toX - 0.23;
    }

    if (this.ICRepComps.includes(from)) {
      //ic,repeater multiple pin is connected to parallel rlc /diode/tp
      let filterPinParallel = [...this.nodeSegments.values()].filter(item =>
        item.from === from &&
        item.to !== to &&
        item.signal !== segment.signal
        && this.endParallelComps.includes(item.to));

      if (this.repeaterComps.includes(from)) {
        filterPinParallel = filterPinParallel.filter(item => {
          const findPin = fromNode.pins.find(it => item.startPin === it.pin);
          if (findPin && findPin.x === fromXPosition) {
            return true;
          }
          return false;
        })
      }

      const pins = this.endParallelCompPrevPins.get(from);
      if (filterPinParallel.length && (pins && pins.length)) {
        _toY = toY - 2;
        newLayer = true;
        _toX = fromNode.x === fromXPosition ? toX - groupStartNode.width - 0.23 : toX - 0.23;
        isNewParallelLayer = true;
        if (!isNewLayer) {
          this.startPosY = this.startPosY + 3;
          _toY = this.startPosY - 2;
        }
        _isExistEndParallelY = false;
      }
    }

    //SCL Y-3
    //SDA Y-3-(signalLayer-1)
    this.layoutNodes.set(to, {
      x: _toX,
      y: _toY,
      width: 0.46,
      height: 1,
      type: toNodeInfo.type,
      pins: toNodeInfo.pins,
      isNewParallelLayer,
      newLayer
    });
    let pins = this.endParallelCompPrevPins.get(from) || [];
    pins = [...new Set(pins.concat(fromPin))];
    this.endParallelCompPrevPins.set(from, pins);
    return { isNewParallelLayer, isExistEndParallelY: _isExistEndParallelY };
  }

  //  get all other paths except the root path
  getAllOtherPaths = (rootPath, root) => {
    const paths = [];

    for (let path of this.data.signalPaths || []) {
      if (path.name !== rootPath.name && path.nodes && path.nodes[0] === root) {
        paths.push({ ...path });
      }
    }

    return paths;
  }

  //calc layoutPaths
  calculatePaths = (root) => {
    const existPaths = [];

    for (let path of this.data.signalPaths) {
      if (root !== path.root) {
        continue;
      }
      const endParallelComp = this.data.endParallelComps[path.root] || [];
      for (let i = 0; i < path.nodes.length - 1; i++) {
        const fromNode = path.nodes[i];
        const toNode = path.nodes[i + 1];

        const fromPos = this.layoutNodes.get(fromNode);
        const toPos = this.layoutNodes.get(toNode);

        if (fromPos && this.ICRepComps.includes(fromNode)) {
          this.getPinToPinPath({ signal: path.signal, node: fromNode, nodePos: fromPos, pathName: path.name, existPaths, type: "from" });
        }
        if (toPos && this.ICRepComps.includes(toNode)) {
          this.getPinToPinPath({ signal: path.signal, node: toNode, nodePos: toPos, pathName: path.name, existPaths, type: "to" });
          //find pins connected same from node
        }

        if (fromPos && toPos) {
          const segment = this.getSegment(path.signal, path.name, fromNode, toNode);

          let startPin = segment.startPin, endPin = segment.endPin;
          if (segment.from === toNode) {
            startPin = segment.endPin;
            endPin = segment.startPin;
          }
          const fromKey = startPin ? `${fromNode}::${startPin}` : `${fromNode}`,
            toKey = endPin ? `${toNode}::${endPin}` : `${toNode}`;
          const signalKey = path.signal;
          if (existPaths.includes(`${signalKey}::${fromKey}::${toKey}`)) continue;

          const fromPin = (fromPos.pins || []).find(pin => pin.pin === startPin);
          const toPin = (toPos.pins || []).find(pin => pin.pin === endPin);
          let fromX = fromPos.x, fromY = fromPos.y, toX = toPos.x, toY = toPos.y;
          if (!this.parallelAllComps.includes(fromNode) && fromPin && fromPin.x) {
            fromX = fromPin.x;
            fromY = fromPin.y;
          }

          if (!this.parallelAllComps.includes(toNode) && toPin && toPin.x) {
            toX = toPin.x;
            toY = toPin.y;
          }

          if ((fromPos.type !== NODE && !this.parallelAllComps.includes(fromNode) && (!fromPin || !fromPin.x)) || (toPos.type !== NODE && !this.parallelAllComps.includes(toNode) && (!toPin || !toPin.x))) {
            continue
          }

          existPaths.push(`${signalKey}::${fromKey}::${toKey}`);
          const unitLength = Math.abs(toX - fromX);
          if (!this.netsMaxLength[segment.net]) {
            this.netsMaxLength[segment.net] = unitLength;
          }

          if (this.netsMaxLength[segment.net] < unitLength) {
            this.netsMaxLength[segment.net] = unitLength;
          }

          let startPinText = null, endPinText = null;
          // Determine pin-aware layer: prefer per-(signal,component::pin) layer if available,
          // otherwise fall back to the signal base layer (or 0).
          const baseSignalLayer = this.signalLayer[path.root] ? this.signalLayer[path.root].get(path.signal) || 0 : 0;
          // compute from/to pin layers without forcing new allocations when possible
          const safeGetPinLayer = (node, pin) => {
            if (!pin) return baseSignalLayer;
            // if already allocated, return it; otherwise fallback to base
            if (this.signalPinLayer[path.root] && this.signalPinLayer[path.root].has(`${node}::${pin}`)) {
              return this.getAndSetSignalLayer(path.root, path.signal, `${node}::${pin}`);
            }
            return baseSignalLayer;
          };

          const fromPinLayer = safeGetPinLayer(fromNode, startPin);
          const toPinLayer = safeGetPinLayer(toNode, endPin);
          let signalLayer = Math.max(baseSignalLayer, fromPinLayer, toPinLayer);

          if (this.parallelAllComps.includes(toNode)) {

            if (endParallelComp.includes(toNode)) {
              let showLength = true
              if (!this.compMap.has(fromNode)) {
                toX = fromX
                toY = toY + 1;
              }

              if (this.compMap.has(fromNode)) {
                if (toPos.newLayer) {
                  toX = toX + 0.23;
                  if (toPos.isNewParallelLayer) {
                    signalLayer = 1;
                  }
                  let fromSignalLayer = this.signalLayer[path.root] ? this.signalLayer[path.root].get(path.signal) || 0 : 0;
                  if (fromPos.isNewParallelLayer) {
                    fromSignalLayer = 1;
                  }
                  const fY = this.parallelAllComps.includes(fromNode) ? fromY + 2 + fromSignalLayer : fromY;
                  const tY = toY + 2 + signalLayer
                  this.layoutPaths.push({
                    from: fromNode,
                    to: toNode,
                    fromX: this.parallelAllComps.includes(fromNode) ? fromX + 0.23 : fromX,
                    fromY: fY,
                    toX,
                    toY: tY,
                    isParallel: false,
                    isDash: true,
                    showLength: true,
                    signal: path.signal,
                    net: segment ? segment.net : '',
                    unitLength,
                    pathName: path.name,
                    length: segment ? segment.length : '',
                    startPinText: fY !== tY ? startPin ? `${fromNode}-${startPin}` : fromNode : null,
                    endPinText: fY !== tY ? endPin ? `${toNode}-${endPin}` : toNode : null,
                  });
                  fromY = toY + 2 + signalLayer;
                  toY = toY + 1;
                  fromX = toX;
                  showLength = false;
                } else {
                  fromX = toX + 0.23;
                  toX = toX + 0.23
                  toY = toY + 1;
                }

                if (this.parallelAllComps.includes(fromNode) && !toPos.newLayer) {
                  let fromSignalLayer = this.signalLayer[path.root] ? this.signalLayer[path.root].get(path.signal) || 0 : 0;
                  if (fromPos.isNewParallelLayer) {
                    fromSignalLayer = 1;
                  }
                  fromY = fromY + 2 + fromSignalLayer;
                  this.layoutPaths.push({
                    from: fromNode,
                    to: toNode,
                    fromX: fromPos.x + 0.23,
                    fromY,
                    toX,
                    toY: fromY,
                    unitLength,
                    signal: path.signal,
                    pathName: path.name,
                    length: segment ? segment.length : '',
                    net: segment ? segment.net : '',
                    fromLayer: fromPos.layer || 0,
                    toLayer: toPos.layer || 0,
                    isParallel: false
                  });
                  showLength = false;
                }
              }
              /* this.parallelRLCs.includes(toNode) && */ this.layoutPaths.push({
                from: fromNode,
                to: toNode,
                fromX,
                fromY,
                toX,
                toY,
                unitLength,
                signal: path.signal,
                pathName: path.name,
                length: segment ? segment.length : '',
                net: segment ? segment.net : '',
                fromLayer: fromPos.layer || 0,
                toLayer: toPos.layer || 0,
                isParallel: true,
                showLength,
              });
              !this.parallelRLCs.includes(toNode) && this.unusedCompNodes.set(`${toNode}::${this.rootIndex}`, { name: toNode, x: toX, y: toPos.y + 1, signal: path.signal });
              continue;
            };

            if (toPos.isNewParallelLayer) {
              signalLayer = 1;
            }
            const _fromY = this.parallelAllComps.includes(fromNode) ? fromY + 2 + signalLayer : fromY;
            if (/* !this.parallelAllComps.includes(fromNode) && !this.seriesRLCs.includes(fromNode) */ /* && this.compMap.has(fromNode)  */ /* && */ _fromY !== (toY + 2 + signalLayer)) {
              startPinText = startPin ? `${fromNode}-${startPin}` : fromNode;
              endPinText = endPin ? `${toNode}-${endPin}` : toNode;
            }
            this.layoutPaths.push({
              from: fromNode,
              to: toNode,
              fromX: this.parallelAllComps.includes(fromNode) ? fromX + 0.23 : fromX,
              fromY: _fromY,
              toX: toX + 0.23,
              toY: toY + 2 + signalLayer,
              unitLength,
              signal: path.signal,
              pathName: path.name,
              length: segment ? segment.length : '',
              net: segment ? segment.net : '',
              fromLayer: fromPos.layer || 0,
              toLayer: toPos.layer || 0,
              isParallel: false,
              startPinText,
              endPinText
            })
            /* this.parallelRLCs.includes(toNode) && */ this.layoutPaths.push({
              from: toNode,
              to: toNode,
              fromX: toX + 0.23,
              fromY: toY + 2 + signalLayer,
              toX: toX + 0.23,
              toY: toY + 1,
              unitLength,
              signal: path.signal,
              pathName: path.name,
              length: segment ? segment.length : '',
              net: segment ? segment.net : '',
              fromLayer: fromPos.layer || 0,
              toLayer: toPos.layer || 0,
              isParallel: true
            })
            !this.parallelRLCs.includes(toNode) && this.unusedCompNodes.set(`${toNode}::${this.rootIndex}`, { name: toNode, x: toPos.x + 0.23, y: toPos.y + 1, signal: path.signal });
            continue;
          }

          if (this.parallelAllComps.includes(fromNode)) {
            fromX = fromX + 0.23;
            let signalLayer = this.signalLayer[path.root] ? this.signalLayer[path.root].get(path.signal) || 0 : 0;
            if (fromPos.isNewParallelLayer) {
              signalLayer = 1;
            }
            fromY = fromY + 2 + signalLayer;
          }

          if (/* !this.parallelAllComps.includes(fromNode) && !this.seriesRLCs.includes(fromNode) */ /* && this.compMap.has(fromNode)  *//*  && */ fromY !== toY) {
            startPinText = startPin ? `${fromNode}-${startPin}` : fromNode;
            endPinText = endPin ? `${toNode}-${endPin}` : toNode;
          }
          this.layoutPaths.push({
            from: fromNode,
            to: toNode,
            fromX,
            fromY,
            toX,
            toY,
            unitLength,
            signal: path.signal,
            pathName: path.name,
            length: segment ? segment.length : '',
            net: segment ? segment.net : '',
            fromLayer: fromPos.layer || 0,
            toLayer: toPos.layer || 0,
            isParallel: false,
            startPinText,
            endPinText
          });

          if (toPos.isY) {
            this.layoutPaths.push({
              from: toNode,
              to: toNode,
              fromX: toX,
              fromY: toY,
              toX,
              toY: toPos.y + 0.23,
              unitLength,
              signal: path.signal,
              pathName: path.name,
              length: segment ? segment.length : '',
              net: segment ? segment.net : '',
              fromLayer: fromPos.layer || 0,
              toLayer: toPos.layer || 0,
              isParallel: false
            });
            if (toY !== toPos.y + 0.23) {
              this.unusedCompNodes.set(`${toNode}::${this.rootIndex}`, { name: toNode, x: toX, y: toY, signal: path.signal });
            }
          }

          if (this.ICRepComps.includes(toNode)) {
            const filterSegments = this.sameSegments.get(`${path.signal}::${fromNode}::${toNode}`) || [];
            if (filterSegments.length > 0) {
              for (let segItem of filterSegments) {
                const toPin = (toPos.pins || []).find(pin => pin.pin === segItem.endPin);
                toX = toPin.x;
                toY = toPin.y;

                this.layoutPaths.push({
                  from: fromNode,
                  to: toNode,
                  fromX,
                  fromY,
                  toX,
                  toY,
                  unitLength: 0,
                  signal: path.signal,
                  pathName: path.name,
                  length: segItem ? segItem.length : '',
                  net: segItem ? segItem.net : '',
                  isParallel: false,
                });
              }
            }
          }
        }
      }
    }
  }

  getPinToPinPath = ({ signal, node, nodePos, pathName, existPaths, type }) => {
    const segment = this.getSegment(signal, pathName, node, node);
    if (!segment) return;
    let startPin = segment.startPin, endPin = segment.endPin;
    const fromKey = startPin ? `${node}::${startPin}` : `${node}`,
      toKey = endPin ? `${node}::${endPin}` : `${node}`;
    const signalKey = signal;

    if (existPaths.includes(`${signalKey}::${fromKey}::${toKey}`)) return;

    const fromPin = (nodePos.pins || []).find(pin => pin.pin === startPin);
    const toPin = (nodePos.pins || []).find(pin => pin.pin === endPin);
    let fromX = nodePos.x, fromY = nodePos.y, toX = nodePos.x, toY = nodePos.y;

    if (fromPin && toPin) {
      fromX = fromPin.x;
      fromY = fromPin.y;
      toX = toPin.x;
      toY = toPin.y;
      fromX && toX && this.layoutPaths.push({
        from: node,
        to: node,
        fromX: type === "to" ? fromX + 0.5 : fromX,
        fromY,
        toX: toX - 0.5,
        toY,
        unitLength: 0.5,
        signal,
        pathName,
        length: segment ? segment.length : '',
        net: segment ? segment.net : '',
        fromLayer: nodePos.layer || 0,
        toLayer: nodePos.layer || 0,
        isParallel: false,
        showCompPinLength: true
      });
      existPaths.push(`${signalKey}::${fromKey}::${toKey}`);
    }
  }

  // get comp and node info
  getNodeInfo = (nodeName) => {
    let type = NODE;
    let pinCount = 1, pins = [];
    const comp = this.compMap.get(nodeName);
    const allSegments = [...this.allSegments, ...this.sameSegmentsList];
    const currRootPins = comp ? (comp.pins || []).filter(item => allSegments.find(it => (it.from === comp.name && it.startPin === item.pin)
      || (it.to === comp.name && it.endPin === item.pin))) : [];
    const signals = comp ? [...new Set(currRootPins.map(pin => pin.signal))] : [];
    if (comp) {
      pinCount = signals.length;
    }
   /*  if (comp && comp.type === COMP_REPEATER) {
      pinCount = signals.length;
    } else */ if (comp && ((RLC_TYPES.includes(comp.type) && !this.repeaterComps.includes(comp.name)) || this.parallelComps.includes(comp.name))) {
      pinCount = 1;
    }
    if (comp) {
      type = comp.type;
      pins = JSON.parse(JSON.stringify((currRootPins || [])))
    }

    const height = comp ? Math.max(this.config.minComponentHeight, (pinCount + 1) * this.config.pinHeight) : 1;

    return {
      type,
      pins,
      height
    };
  }

  // calculate Bounds
  calculateBounds = () => {
    let maxX = 0, maxY = 0;

    for (let pos of this.layoutComps.values()) {
      maxX = Math.max(maxX, pos.x + pos.width || 0);
      maxY = Math.max(maxY, pos.y + pos.height || 0);
    };
    for (let item of this.layoutPaths) {
      maxX = Math.max(maxX, item.fromX, item.toX);
      maxY = Math.max(maxY, item.fromY, item.toY);
    };
    this.bounds = { width: maxX > 0 ? (maxX + 2) * this.config.unitSize : 0, height: maxY > 0 ? (maxY + 2) * this.config.unitSize : 0 };
  }

  getResults = () => {
    return {
      layoutNodes: this.layoutNodes,
      layoutComps: this.layoutComps,
      nodes: this.nodes,
      layoutPaths: this.layoutPaths,
      unitSize: this.config.unitSize,
      bounds: this.bounds,
      netsMaxLength: this.netsMaxLength,
      parallelComps: this.parallelComps,
      unusedCompNodes: this.unusedCompNodes
    };
  }
}

export default CalculatePathPosition;
